// src/component/hooks/useFeedbackTickets.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApiService from './useApiService';
import {
  fetchFeedbackThreads,
  mapThread,
} from '../../services/feedbackSupport';

const DEFAULT_FILTERS = {
  status: '',
  email: '',
};

const DEFAULT_PAGINATION = {
  page: 0,
  pageSize: 20,
};

const extractError = (error) => {
  const apiMessage = error?.response?.data?.error?.message;
  if (apiMessage) return apiMessage;
  const fallback = error?.message;
  return fallback || 'Feedback ticket listesi yüklenemedi.';
};

export default function useFeedbackTickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { executeGet } = useApiService();
  const getRef = useRef(executeGet);
  useEffect(() => {
    getRef.current = executeGet;
  }, [executeGet]);

  const parseInitialFilters = useCallback(() => {
    const status = searchParams.get('status') || '';
    const email = searchParams.get('email') || '';
    return {
      status,
      email,
    };
  }, [searchParams]);

  const parseInitialPagination = useCallback(() => {
    const pageParam = Number.parseInt(searchParams.get('page') || '0', 10);
    const limitParam = Number.parseInt(searchParams.get('limit') || String(DEFAULT_PAGINATION.pageSize), 10);
    return {
      page: Number.isNaN(pageParam) || pageParam < 0 ? 0 : pageParam,
      pageSize: Number.isNaN(limitParam) ? DEFAULT_PAGINATION.pageSize : Math.min(Math.max(limitParam, 1), 200),
    };
  }, [searchParams]);

  const [filters, setFilters] = useState(() => parseInitialFilters());
  const [pagination, setPagination] = useState(() => parseInitialPagination());

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const params = {
          limit: pagination.pageSize,
          offset: pagination.page * pagination.pageSize,
          status: filters.status || undefined,
          email: filters.email?.trim() || undefined,
        };

        const res = await fetchFeedbackThreads(getRef.current, params);
        if (!alive) return;

        const items = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : [];

        const mapped = items
          .map(mapThread)
          .filter(Boolean);

        setRows(mapped);
        const totalCount = res?.total ?? res?.meta?.total ?? mapped.length;
        setTotal(typeof totalCount === 'number' ? totalCount : mapped.length);
      } catch (err) {
        if (!alive) return;
        setError(extractError(err));
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [filters, pagination.page, pagination.pageSize, reloadKey]);

  const applyFilters = useCallback((nextFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...nextFilters,
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  useEffect(() => {
    const nextFilters = parseInitialFilters();
    setFilters((prev) => {
      if (prev.status === nextFilters.status && prev.email === nextFilters.email) {
        return prev;
      }
      return nextFilters;
    });
    const nextPagination = parseInitialPagination();
    setPagination((prev) => {
      if (prev.page === nextPagination.page && prev.pageSize === nextPagination.pageSize) {
        return prev;
      }
      return nextPagination;
    });
  }, [parseInitialFilters, parseInitialPagination]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.email) params.set('email', filters.email);
    if (filters.status) params.set('status', filters.status);
    if (pagination.page) params.set('page', String(pagination.page));
    if (pagination.pageSize !== DEFAULT_PAGINATION.pageSize) {
      params.set('limit', String(pagination.pageSize));
    }
    const nextStr = params.toString();
    const currentStr = searchParams.toString();
    if (nextStr !== currentStr) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, pagination.page, pagination.pageSize, searchParams, setSearchParams]);

  return {
    rows,
    total,
    loading,
    error,
    filters,
    pagination,
    setPagination,
    applyFilters,
    resetFilters,
    refresh,
  };
}
